// Client Portal — shared service endpoints, token storage, and fetch helpers.
// Extracted from App.js so per-product portal modules import one source of
// truth instead of closing over App-scope globals.

export const AUTH_URL = process.env.REACT_APP_AUTH_URL || "/auth";
export const N8N_BASE = "https://n8n.hyveappliedintelligence.com";
export const FIELDIQ_API_URL =
  "https://fieldiq-api.hyveappliedintelligence.com";
export const ARTICLE_GEN_URL =
  process.env.REACT_APP_ARTICLE_GEN_URL || "/article";

export const EMAIL_INTEL_URL =
  process.env.REACT_APP_EMAIL_INTEL_URL ||
  "https://email-intel.hyveappliedintelligence.com";

export const DISTRIBUTION_URL =
  process.env.REACT_APP_DISTRIBUTION_URL ||
  "https://distribution.hyveappliedintelligence.com";

export const BUILDIQ_URL =
  process.env.REACT_APP_BUILDIQ_URL ||
  "https://buildiq.hyveappliedintelligence.com";

export const MARKETING_URL =
  process.env.REACT_APP_MARKETING_URL ||
  "https://marketing.hyveappliedintelligence.com";

export const getToken = () => localStorage.getItem("hyveai_token");

export const authFetch = async (endpoint, options = {}) => {
  const method = options.method || "GET";
  const headers = {};
  if (method !== "GET") headers["Content-Type"] = "application/json";
  const token = getToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const res = await fetch(`${AUTH_URL}${endpoint}`, {
    ...options,
    headers: { ...headers, ...options.headers },
  });
  if (res.status === 401) {
    localStorage.removeItem("hyveai_token");
    window.location.reload();
    return {};
  }
  if (!res.ok) {
    const t = await res.text();
    console.error(`API ${res.status}:`, t);
    return { error: t };
  }
  try {
    return await res.json();
  } catch {
    return {};
  }
};

// Marketing Director service. Returns { ok, status, data } — 4xx bodies carry
// actionable detail (readiness unmet lists, validation issues) the UI renders.
export const marketingFetch = async (endpoint, options = {}) => {
  const res = await fetch(`${MARKETING_URL}${endpoint}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getToken()}`,
      ...(options.headers || {}),
    },
  });
  let data = null;
  try {
    data = await res.json();
  } catch {
    data = null;
  }
  return { ok: res.ok, status: res.status, data };
};

export const emailIntelFetch = async (endpoint, options = {}) => {
  const t = getToken();
  const res = await fetch(`${EMAIL_INTEL_URL}${endpoint}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${t}`,
      ...(options.headers || {}),
    },
  });
  if (!res.ok) throw new Error(`emailIntelFetch error ${res.status}`);
  return res.json();
};

// Distribution broker fetch — errors carry the HTTP status and the broker's
// structured detail (e.g. {status: "not_entitled"}) so the portal can render
// locked/over-limit states instead of a generic failure.
export const distributionFetch = async (endpoint, options = {}) => {
  const t = getToken();
  const res = await fetch(`${DISTRIBUTION_URL}${endpoint}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${t}`,
      ...(options.headers || {}),
    },
  });
  if (!res.ok) {
    let detail = null;
    try {
      detail = (await res.json()).detail;
    } catch {
      /* non-JSON error body */
    }
    const err = new Error(`distributionFetch error ${res.status}`);
    err.status = res.status;
    err.detail = detail;
    throw err;
  }
  return res.json();
};

// Like authFetch but sends FormData (no Content-Type header — browser sets multipart boundary).
export const authUpload = async (endpoint, formData) => {
  const token = getToken();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const res = await fetch(`${AUTH_URL}${endpoint}`, {
    method: "POST",
    headers,
    body: formData,
  });
  if (res.status === 401) {
    localStorage.removeItem("hyveai_token");
    window.location.reload();
    return {};
  }
  if (!res.ok) {
    const t = await res.text();
    return { error: t };
  }
  try {
    return await res.json();
  } catch {
    return {};
  }
};
